import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState, useEffect } from 'react';
import { useFormik, FormikProvider } from 'formik';
import * as Yup from 'yup';
import Drawer from '@mui/material/Drawer';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import CustomLoader from './CustomLoader';
import AppButton from './AppButton';
import { CalendarDate } from '@internationalized/date';
import '../App.css';
import { TextInput, Dropdown } from './FormInputs';
import { useSelector } from 'react-redux';
import { selectToken } from '../redux/authSlice';
import CustomDatePicker from './daterangepicker/CustomDatePicker';
const drawerWidth = 600;
const getValidationSchema = (takenOrderings, isEditMode, currentOrdering) => {
    return Yup.object({
        name: Yup.string()
            .trim()
            .required('Holiday name is required')
            .max(50, 'Holiday name cannot exceed 50 characters')
            .test('not-only-numbers', 'Holiday name cannot contain only numbers', function (value) {
            if (!value)
                return true;
            return !/^\d+$/.test(value.replace(/\s/g, ''));
        }),
        ordering: Yup.number()
            .required('Ordering is required')
            .positive('Ordering must be a positive number')
            .integer('Ordering must be a whole number.'),
        start_date: Yup.string().required('Current year start date is required'),
        end_date: Yup.string()
            .required('Current year end date is required')
            .test('end-date-after-start', 'End date must be after or equal to start date', function (value) {
            const { start_date } = this.parent;
            if (!start_date || !value)
                return true;
            return new Date(value) >= new Date(start_date);
        }),
        prev_start_date: Yup.string()
            .required('Previous year start date is required')
            .test('prev-year-different', 'The previous year dates must fall in a different calendar year from the current year dates.', function (value) {
            const { start_date } = this.parent;
            if (!start_date || !value)
                return true;
            const currentYear = new Date(start_date).getFullYear();
            const prevYear = new Date(value).getFullYear();
            return currentYear !== prevYear;
        }),
        prev_end_date: Yup.string()
            .required('Previous year end date is required')
            .test('prev-end-after-prev-start', 'Previous year end date must be after or equal to previous year start date', function (value) {
            const { prev_start_date } = this.parent;
            if (!prev_start_date || !value)
                return true;
            return new Date(value) >= new Date(prev_start_date);
        }),
        status: Yup.string()
            .required('Status is required')
            .oneOf(['active', 'inactive'], 'Please select a valid status.'),
    });
};
// Helper function to format CalendarDate to YYYY-MM-DD
const formatCalendarDate = (date) => {
    if (!date)
        return '';
    const year = date.year;
    const month = String(date.month).padStart(2, '0');
    const day = String(date.day).padStart(2, '0');
    return `${year}-${month}-${day}`;
};
// Helper function to parse YYYY-MM-DD to CalendarDate
const parseToCalendarDate = (dateStr) => {
    if (!dateStr)
        return null;
    const [year, month, day] = dateStr.split('-').map(Number);
    return new CalendarDate(year, month, day);
};
function HolidayDrawer({ open, onClose, holidayData = null, onSave, totalHolidaysCount = 0, }) {
    const token = useSelector(selectToken);
    const isEditMode = !!holidayData;
    const [orderingOptions, setOrderingOptions] = useState([]);
    const [takenOrderings, setTakenOrderings] = useState(new Set());
    useEffect(() => {
        const generateOrderingOptions = async () => {
            if (!open)
                return;
            try {
                const maxOrdering = totalHolidaysCount;
                const options = [];
                // While editing: options should match existing entries count (1..total)
                // While creating: allow placing at the end (1..total+1)
                const upperLimit = isEditMode ? maxOrdering : maxOrdering + 1;
                for (let i = 1; i <= upperLimit; i++) {
                    options.push({
                        value: i,
                        label: i.toString(),
                    });
                }
                setOrderingOptions(options);
                // In add mode, set the next available ordering number
                if (!isEditMode && !formik.values.ordering) {
                    const nextAvailable = maxOrdering + 1;
                    formik.setFieldValue('ordering', nextAvailable);
                }
            }
            catch (error) {
                console.error('Error generating ordering options:', error);
                const fallbackOptions = [];
                for (let i = 1; i <= 10; i++) {
                    fallbackOptions.push({ value: i, label: i.toString() });
                }
                setOrderingOptions(fallbackOptions);
            }
        };
        generateOrderingOptions();
    }, [open, totalHolidaysCount, isEditMode]);
    const formik = useFormik({
        initialValues: {
            name: holidayData?.name || '',
            ordering: holidayData?.ordering || '', // Keep as empty string if not present
            start_date: holidayData?.start_date || '',
            end_date: holidayData?.end_date || '',
            prev_start_date: holidayData?.prev_start_date || '',
            prev_end_date: holidayData?.prev_end_date || '',
            status: holidayData?.status || 'active',
        },
        enableReinitialize: true,
        validationSchema: getValidationSchema(takenOrderings, isEditMode, holidayData?.ordering),
        validateOnBlur: true,
        validateOnChange: true,
        validate: (values) => {
            // Custom validation to handle empty ordering in edit mode
            const errors = {};
            // Only validate ordering if it's empty and not in edit mode
            if (!values.ordering && !isEditMode) {
                errors.ordering = 'Ordering is required';
            }
            return errors;
        },
        onSubmit: async (values, { resetForm, setSubmitting, setErrors }) => {
            try {
                if (onSave) {
                    const result = await onSave(values);
                    if (result === true) {
                        if (!isEditMode) {
                            resetForm();
                        }
                    }
                    else if (result &&
                        typeof result === 'object' &&
                        'errors' in result) {
                        // Handle API validation errors
                        setErrors(result.errors);
                    }
                }
            }
            catch (e) {
                console.error('save holiday error', e);
            }
            finally {
                setSubmitting(false);
            }
        },
    });
    const handleChange = (name, e) => {
        const value = e?.target?.value || '';
        formik.setFieldValue(name, value);
    };
    const handleOrderingChange = (value) => {
        const v = Array.isArray(value) ? value[0] : value;
        formik.setFieldValue('ordering', v === '' ? '' : Number(v));
        formik.setFieldTouched('ordering', true, false);
    };
    const handleStatusChange = (value) => {
        const v = Array.isArray(value) ? value[0] : value;
        formik.setFieldValue('status', v ?? '');
        formik.setFieldTouched('status', true, false);
    };
    const handleBlur = (name) => {
        formik.setFieldTouched(name, true);
    };
    const handleDateChange = (field, date) => {
        formik.setFieldValue(field, date ? formatCalendarDate(date) : '');
        formik.setFieldTouched(field, true, false);
        if (field === 'start_date') {
            if (formik.values.end_date) {
                formik.validateField('end_date');
            }
            if (formik.values.prev_start_date) {
                formik.validateField('prev_start_date');
            }
        }
        if (field === 'end_date' && formik.values.start_date) {
            formik.validateField('end_date');
        }
        if (field === 'prev_start_date') {
            if (formik.values.prev_end_date) {
                formik.validateField('prev_end_date');
            }
            if (formik.values.start_date) {
                formik.validateField('prev_start_date');
            }
        }
        if (field === 'prev_end_date' && formik.values.prev_start_date) {
            formik.validateField('prev_end_date');
        }
    };
    // Handle date range change for Current Year Promotion Dates
    const handleCurrentYearDateRangeChange = (range) => {
        if (range && range.start && range.end) {
            const startDateStr = formatCalendarDate(range.start);
            const endDateStr = formatCalendarDate(range.end);
            // Auto-fill the previous year range: same month/day, year - 1
            const prevStartDateStr = formatCalendarDate(range.start.subtract({ years: 1 }));
            const prevEndDateStr = formatCalendarDate(range.end.subtract({ years: 1 }));
            // Set both values atomically using setValues
            formik.setValues({
                ...formik.values,
                start_date: startDateStr,
                end_date: endDateStr,
                prev_start_date: prevStartDateStr,
                prev_end_date: prevEndDateStr,
            }, false);
            // Mark all fields as touched
            formik.setFieldTouched('start_date', true, false);
            formik.setFieldTouched('end_date', true, false);
            formik.setFieldTouched('prev_start_date', true, false);
            formik.setFieldTouched('prev_end_date', true, false);
            // Clear errors first since we're setting valid dates
            formik.setFieldError('start_date', undefined);
            formik.setFieldError('end_date', undefined);
            formik.setFieldError('prev_start_date', undefined);
            formik.setFieldError('prev_end_date', undefined);
            // Validate fields after state update
            setTimeout(() => {
                formik.validateField('start_date');
                formik.validateField('end_date');
                formik.validateField('prev_start_date');
                formik.validateField('prev_end_date');
            }, 10);
        }
        else {
            // Clear both values
            formik.setValues({
                ...formik.values,
                start_date: '',
                end_date: '',
            }, false);
            formik.setFieldTouched('start_date', true, false);
            formik.setFieldTouched('end_date', true, false);
            // Trigger validation to show required errors
            setTimeout(() => {
                formik.validateField('start_date');
                formik.validateField('end_date');
            }, 10);
        }
    };
    // Get current year date range for CustomDatePicker
    const getCurrentYearDateRange = () => {
        const startDate = parseToCalendarDate(formik.values.start_date);
        const endDate = parseToCalendarDate(formik.values.end_date);
        if (startDate && endDate) {
            return { start: startDate, end: endDate };
        }
        return null;
    };
    // Handle date range change for Previous Year Promotion Dates
    const handlePreviousYearDateRangeChange = (range) => {
        if (range && range.start && range.end) {
            const startDateStr = formatCalendarDate(range.start);
            const endDateStr = formatCalendarDate(range.end);
            // Set both values atomically using setValues
            formik.setValues({
                ...formik.values,
                prev_start_date: startDateStr,
                prev_end_date: endDateStr,
            }, false);
            // Mark both fields as touched
            formik.setFieldTouched('prev_start_date', true, false);
            formik.setFieldTouched('prev_end_date', true, false);
            // Clear errors first since we're setting valid dates
            formik.setFieldError('prev_start_date', undefined);
            formik.setFieldError('prev_end_date', undefined);
            // Validate both fields after state update
            setTimeout(() => {
                formik.validateField('prev_start_date');
                formik.validateField('prev_end_date');
                // Also validate current year start date if it exists (for year comparison)
                if (formik.values.start_date) {
                    formik.validateField('start_date');
                }
            }, 10);
        }
        else {
            // Clear both values
            formik.setValues({
                ...formik.values,
                prev_start_date: '',
                prev_end_date: '',
            }, false);
            formik.setFieldTouched('prev_start_date', true, false);
            formik.setFieldTouched('prev_end_date', true, false);
            // Trigger validation to show required errors
            setTimeout(() => {
                formik.validateField('prev_start_date');
                formik.validateField('prev_end_date');
            }, 10);
        }
    };
    // Get previous year date range for CustomDatePicker
    const getPreviousYearDateRange = () => {
        const startDate = parseToCalendarDate(formik.values.prev_start_date);
        const endDate = parseToCalendarDate(formik.values.prev_end_date);
        if (startDate && endDate) {
            return { start: startDate, end: endDate };
        }
        return null;
    };
    return (_jsx(Drawer, { anchor: 'right', className: 'user-list-edit', open: open, onClose: onClose, sx: {
            width: drawerWidth,
            flexShrink: 0,
            '& .MuiDrawer-paper': {
                width: drawerWidth,
                boxSizing: 'border-box',
            },
        }, children: _jsx(FormikProvider, { value: formik, children: _jsxs(Box, { sx: {
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%',
                    backgroundColor: '#fdfdfd',
                }, children: [_jsxs(Box, { sx: {
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            padding: '10px',
                            height: '60px',
                            borderBottom: '1px solid #e9eaeb',
                            backgroundColor: '#fafafa',
                        }, children: [_jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: '10px' }, children: [_jsx(IconButton, { onClick: onClose, size: 'small', disableRipple: true, sx: { p: 0, '&:hover': { backgroundColor: 'transparent' } }, children: _jsx(CloseIcon, { sx: { fontSize: 20, color: '#71717A' } }) }), _jsx("div", { className: 'line' }), _jsx(Typography, { sx: {
                                            fontSize: '16px',
                                            fontWeight: 500,
                                            color: '#18181b',
                                            lineHeight: '24px',
                                        }, className: 'drawer-main-title', children: isEditMode ? 'Edit Holiday' : 'Add New Holiday' })] }), _jsx(AppButton, { variant: 'contained', onClick: () => formik.handleSubmit(), startIcon: _jsx(SaveIcon, {}), disabled: formik.isSubmitting, color: 'primary', className: 'app-common-button', children: formik.isSubmitting ? (_jsx(CustomLoader, { sx: { color: 'white' }, size: 18 })) : ('Save') })] }), _jsxs(Box, { className: 'form-input-root gap-responsive holiday-section-drawer', component: 'form', onSubmit: formik.handleSubmit, sx: {
                            flex: 1,
                            overflowY: 'auto',
                            padding: '20px',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '20px',
                            position: 'relative',
                        }, children: [_jsxs(Box, { sx: { display: 'flex', flexDirection: 'column', gap: '20px' }, children: [_jsxs(Box, { sx: { display: 'flex', flexDirection: 'column', gap: '10px' }, children: [_jsx(Typography, { sx: {
                                                    fontSize: '18px',
                                                    fontWeight: 600,
                                                    color: '#ff008e',
                                                    lineHeight: '28px',
                                                }, className: 'field-drawer-title', children: "Holiday Information" }), _jsx(Box, { sx: {
                                                    height: '1px',
                                                    backgroundColor: '#e9eaeb',
                                                    width: '100%',
                                                } })] }), _jsxs(Box, { sx: {
                                            display: 'grid',
                                            gridTemplateColumns: {
                                                xs: '1fr', // Mobile
                                                sm: '1fr 1fr', // Tablet
                                                md: '1fr 1fr 1fr', // Desktop
                                            },
                                            gap: '20px',
                                            alignItems: 'flex-start',
                                        }, children: [_jsx(Box, { children: _jsx(TextInput, { label: 'Holiday Name', type: 'text', required: true, maxLength: 50, value: formik.values.name, placeholder: 'Enter holiday name', onChange: (e) => handleChange('name', e), onBlur: () => handleBlur('name'), autoComplete: 'off', error: Boolean(formik.touched.name && formik.errors.name), errorMessage: formik.touched.name && formik.errors.name
                                                        ? formik.errors.name
                                                        : undefined }) }), _jsx(Box, { children: _jsx(Dropdown, { label: 'Ordering', value: formik.values.ordering !== '' && formik.values.ordering !== undefined ? String(formik.values.ordering) : '', onChange: handleOrderingChange, placeholder: 'Select', options: orderingOptions.map((opt) => ({
                                                        value: String(opt.value),
                                                        label: opt.label,
                                                    })), required: !isEditMode, error: Boolean(formik.touched.ordering && formik.errors.ordering), errorMessage: formik.touched.ordering && formik.errors.ordering
                                                        ? formik.errors.ordering
                                                        : undefined }) }), _jsx(Box, { children: _jsx(Dropdown, { label: 'Status', value: formik.values.status, onChange: handleStatusChange, placeholder: 'Select status', options: [
                                                        { value: 'active', label: 'Active' },
                                                        { value: 'inactive', label: 'Inactive' },
                                                    ], required: true, error: Boolean(formik.touched.status && formik.errors.status), errorMessage: formik.touched.status && formik.errors.status
                                                        ? formik.errors.status
                                                        : undefined }) })] })] }), _jsxs(Box, { sx: { display: 'flex', flexDirection: 'column', gap: '20px' }, children: [_jsxs(Box, { sx: { display: 'flex', flexDirection: 'column', gap: '10px' }, children: [_jsx(Typography, { sx: {
                                                    fontSize: '18px',
                                                    fontWeight: 600,
                                                    color: '#ff008e',
                                                    lineHeight: '28px',
                                                }, className: 'field-drawer-title', children: "Promotion Dates" }), _jsx(Box, { sx: {
                                                    height: '1px',
                                                    backgroundColor: '#e9eaeb',
                                                    width: '100%',
                                                } })] }), _jsxs(Box, { sx: {
                                            width: '100%',
                                            display: 'grid',
                                            gridTemplateColumns: {
                                                xs: '1fr',
                                                sm: '1fr',
                                                md: '1fr 1fr',
                                            },
                                            columnGap: '20px',
                                            rowGap: '20px',
                                        }, children: [_jsxs(Box, { sx: { width: '100%' }, children: [_jsxs(Typography, { sx: { mb: 1, fontSize: '14px', fontWeight: 500 }, children: ["Current Year Date Range", ' ', _jsx("span", { style: { color: '#ff008e' }, children: "*" })] }), _jsxs(Box, { sx: {
                                                            width: '100%',
                                                            '& .custom-date-picker-wrapper': {
                                                                width: '100%',
                                                                display: 'block',
                                                                position: 'static',
                                                            },
                                                            '& .date-picker-trigger-container': {
                                                                width: '100%',
                                                            },
                                                            '& .date-picker-trigger-container .date-picker-trigger-field': {
                                                                borderLeft: '1px solid #e0e0e0',
                                                                borderRight: '1px solid #e0e0e0',
                                                                borderRadius: '8px',
                                                            },
                                                        }, children: [_jsx(CustomDatePicker, { value: getCurrentYearDateRange(), onChange: handleCurrentYearDateRangeChange, placeholder: 'Select current year date range', comparison: false, maxNextYears: 1, size: 'compact', allowAllDates: true }), (() => {
                                                                const startError = formik.touched.start_date && formik.errors.start_date;
                                                                const endError = formik.touched.end_date && formik.errors.end_date;
                                                                const startErrorStr = startError
                                                                    ? formik.errors.start_date
                                                                    : '';
                                                                const endErrorStr = endError
                                                                    ? formik.errors.end_date
                                                                    : '';
                                                                const bothRequired = startErrorStr.includes('required') &&
                                                                    endErrorStr.includes('required');
                                                                if (bothRequired) {
                                                                    return (_jsx(Typography, { sx: { color: 'red', fontSize: '12px', mt: 1 }, children: "Current year date range is required" }));
                                                                }
                                                                else if (startError || endError) {
                                                                    const errorMessage = startErrorStr || endErrorStr;
                                                                    return (_jsx(Typography, { sx: { color: 'red', fontSize: '12px', mt: 1 }, children: errorMessage }));
                                                                }
                                                                return null;
                                                            })()] })] }), _jsxs(Box, { sx: { width: '100%' }, children: [_jsxs(Typography, { sx: { mb: 1, fontSize: '14px', fontWeight: 500 }, children: ["Previous Year Date Range", ' ', _jsx("span", { style: { color: '#ff008e' }, children: "*" })] }), _jsxs(Box, { sx: {
                                                            width: '100%',
                                                            '& .custom-date-picker-wrapper': {
                                                                width: '100%',
                                                                display: 'block',
                                                                position: 'static',
                                                            },
                                                            '& .date-picker-trigger-container': {
                                                                width: '100%',
                                                            },
                                                            '& .date-picker-trigger-container .date-picker-trigger-field': {
                                                                borderLeft: '1px solid #e0e0e0',
                                                                borderRight: '1px solid #e0e0e0',
                                                                borderRadius: '8px',
                                                            },
                                                        }, children: [_jsx(CustomDatePicker, { value: getPreviousYearDateRange(), onChange: handlePreviousYearDateRangeChange, placeholder: 'Select previous year date range', comparison: false, maxPastYears: 1, size: 'compact', allowAllDates: true }), (() => {
                                                                const startError = formik.touched.prev_start_date &&
                                                                    formik.errors.prev_start_date;
                                                                const endError = formik.touched.prev_end_date &&
                                                                    formik.errors.prev_end_date;
                                                                const startErrorStr = startError
                                                                    ? formik.errors.prev_start_date
                                                                    : '';
                                                                const endErrorStr = endError
                                                                    ? formik.errors.prev_end_date
                                                                    : '';
                                                                const bothRequired = startErrorStr.includes('required') &&
                                                                    endErrorStr.includes('required');
                                                                if (bothRequired) {
                                                                    return (_jsx(Typography, { sx: { color: 'red', fontSize: '12px', mt: 1 }, children: "Previous year date range is required" }));
                                                                }
                                                                else if (startError || endError) {
                                                                    const errorMessage = startErrorStr || endErrorStr;
                                                                    return (_jsx(Typography, { sx: { color: 'red', fontSize: '12px', mt: 1 }, children: errorMessage }));
                                                                }
                                                                return null;
                                                            })()] })] })] })] })] })] }) }) }));
}
export default React.memo(HolidayDrawer);
